// PHA-567 / PHA-574: top-of-feed band, rendered between live acute buckets
// and the rest of TaskBar in SmartHubPanel. Stacking rule (do not break):
//   live acute  →  AiInsightsBand  →  rest of TaskBar
// AI Insights NEVER renders above live acute alerts. The backend overlay
// further bumps any AI insight whose patient is currently in an acute bucket
// to the top of the band with a "● LIVE" pip.

import React, { useMemo, useState } from 'react';
import { Badge, Box, Card, Flex, Heading, Text } from '@radix-ui/themes';
import { LightningBoltIcon, ChevronDownIcon, ChevronUpIcon } from '@radix-ui/react-icons';
import { useAiInsights } from './useAiInsights';
import AiInsightCard from './AiInsightCard';
import AiInsightDrawer from './AiInsightDrawer';
import type { PatientAiInsight } from '../types';

interface AiInsightsBandProps {
  organizationId?: string;
  // When the panel is patient-scoped, render only that patient's insight
  // (no band; the parent handles inline display via the drawer body).
  patientId?: string;
}

export const AiInsightsBand: React.FC<AiInsightsBandProps> = ({ organizationId, patientId }) => {
  const { enabled, insights, loading, lastUpdated, refetch } = useAiInsights(organizationId);
  const [selected, setSelected] = useState<PatientAiInsight | null>(null);
  const [showStable, setShowStable] = useState(false);

  // Filter to the patient when scoped; otherwise show all but split stable
  // patients into a collapsed group (so the headline only shows critical/watch).
  const visible = useMemo(() => {
    if (patientId) return insights.filter((i) => i.patient_id === patientId);
    return insights;
  }, [insights, patientId]);

  const headline = useMemo(
    () => visible.filter((i) => i.risk_tier !== 'stable' || i.live_signal),
    [visible],
  );
  const stable = useMemo(
    () => visible.filter((i) => i.risk_tier === 'stable' && !i.live_signal),
    [visible],
  );

  if (!enabled) return null;
  if (!loading && visible.length === 0) {
    return (
      <Card variant="surface">
        <Flex align="center" gap="2">
          <LightningBoltIcon />
          <Text size="2" color="gray">
            AI Insights — no patients computed yet for this organization.
          </Text>
          <Badge color="gray" variant="soft" size="1">Beta</Badge>
        </Flex>
      </Card>
    );
  }

  return (
    <>
      <Card variant="classic">
        <Flex direction="column" gap="2">
          <Flex align="center" justify="between" gap="2">
            <Flex align="center" gap="2">
              <LightningBoltIcon />
              <Heading size="2">
                AI Insights{' '}
                {headline.length > 0 && (
                  <Text size="1" color="gray" weight="regular">
                    — {headline.length} need attention
                  </Text>
                )}
              </Heading>
              <Badge color="gray" variant="soft" size="1">Beta</Badge>
            </Flex>
            {lastUpdated && (
              <Text size="1" color="gray">
                {lastUpdated.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
              </Text>
            )}
          </Flex>

          <Box>
            {headline.map((i) => (
              <AiInsightCard key={i.insight_id} insight={i} onSelect={setSelected} />
            ))}
          </Box>

          {!patientId && stable.length > 0 && (
            <>
              <Box
                role="button"
                tabIndex={0}
                onClick={() => setShowStable((s) => !s)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    setShowStable((s) => !s);
                  }
                }}
                style={{ cursor: 'pointer', padding: '6px 0' }}
              >
                <Flex align="center" gap="1">
                  {showStable ? <ChevronUpIcon /> : <ChevronDownIcon />}
                  <Text size="1" color="gray">
                    + {stable.length} stable patients
                  </Text>
                </Flex>
              </Box>
              {showStable && (
                <Box>
                  {stable.map((i) => (
                    <AiInsightCard key={i.insight_id} insight={i} onSelect={setSelected} />
                  ))}
                </Box>
              )}
            </>
          )}
        </Flex>
      </Card>

      <AiInsightDrawer
        insight={selected}
        organizationId={organizationId}
        open={selected !== null}
        onClose={() => setSelected(null)}
        onRefreshed={(next) => {
          setSelected(next);
          refetch();
        }}
      />
    </>
  );
};

export default AiInsightsBand;
